/**
 * One-shot cues: the debrief, the medal, the promotion, and the memorial. Played once over (or
 * instead of) whatever bed is running.
 *
 * The memorial motif is ORIGINAL. It is deliberately not a bugle call: where "Taps" climbs a
 * major triad in dotted rhythm, this is a slow stepwise lament in D minor that comes to rest,
 * the first time, on the unresolved seventh - and only the second time finds the tonic.
 */

import { type Song, track } from './pattern';

const REST = '. . . . . . . . . . . . . . . .';
const HOLD = '- - - - - - - - - - - - - - - -';

const MEMORIAL_MELODY = [
  'D4 - - - - - - - E4 - - - F4 - - -',
  'G4 - - - - - - - - - - - F4 - E4 -',
  'F4 - - - - - - - E4 - - - D4 - - -',
  'C4 - - - - - - - - - - - . . . .',
  'D4 - - - - - - - F4 - - - G4 - - -',
  'A4 - - - - - - - - - - - Bb4 - A4 -',
  'G4 - - - - - - - F4 - - - E4 - - -',
  'D4 - - - - - - - - - - - - - - -',
];
const MEMORIAL_DRUM = ['o . . . . . . . . . . . . . . .', REST, 'o . . . . . . . . . . . . . . .', REST];

export const MEMORIAL: Song = {
  name: 'memorial',
  bpm: 60,
  steps: 8 * 16,
  tracks: [
    track('bugle', 'bugle', 'always', 0.9, MEMORIAL_MELODY),
    track('drone', 'pad', 'always', 1, ['D2 - - - - - - - - - - - - - - -', HOLD, HOLD, HOLD,
      'D2 - - - - - - - - - - - - - - -', HOLD, HOLD, HOLD]),
    track('fifth', 'pad', 'always', 0.6, ['A2 - - - - - - - - - - - - - - -', HOLD, HOLD, HOLD,
      'A2 - - - - - - - - - - - - - - -', HOLD, HOLD, HOLD]),
    track('drum', 'kick', 'always', 0.5, [...MEMORIAL_DRUM, ...MEMORIAL_DRUM]),
  ],
};

/** KIA at the end of a mission: the memorial's first phrase only. The full motif belongs to the wall. */
export const DEBRIEF_KIA: Song = {
  name: 'debrief-kia',
  bpm: 60,
  steps: 4 * 16,
  tracks: [
    track('bugle', 'bugle', 'always', 0.9, MEMORIAL_MELODY.slice(0, 4)),
    track('drone', 'pad', 'always', 1, ['D2 - - - - - - - - - - - - - - -', HOLD, HOLD, HOLD]),
    track('drum', 'kick', 'always', 0.5, MEMORIAL_DRUM),
  ],
};

/** Mission accomplished and out: the theme's cadence, in the major. */
export const DEBRIEF_SUCCESS: Song = {
  name: 'debrief-success',
  bpm: 104,
  steps: 2 * 16,
  tracks: [
    track('lead', 'lead', 'always', 0.9, ['A4 - - A4 A4 - D5 - F#5 - - - - - E5 -',
      'D5^ - - - - - - - - - - - . . . .']),
    track('harmony', 'arp', 'always', 0.9, ['F#4 - - F#4 F#4 - A4 - D5 - - - - - C#5 -',
      'A4 - - - - - - - - - - - . . . .']),
    track('bass', 'bass', 'always', 0.8, ['D2 . D2 . D2 . D3 . A1 . A1 . A1 . A2 .',
      'D2 - - - - - - - - - - - . . . .']),
    track('snare', 'snare', 'always', 0.7, ['X . x x X . x x o o o o x x X X', 'X . . . . . . . . . . . . . . .']),
    track('kick', 'kick', 'always', 0.7, ['X . . . . . . . X . . . . . . .', 'X . . . . . . . . . . . . . . .']),
  ],
};

/** Out alive but the job undone, or a prisoner: subdued, falling, unfinished. */
export const DEBRIEF_LOSS: Song = {
  name: 'debrief-loss',
  bpm: 72,
  steps: 2 * 16,
  tracks: [
    track('bugle', 'bugle', 'always', 0.8, ['D4 - - - - - - - C4 - - - - - - -', 'Bb3 - - - - - - - A3 - - - - - - -']),
    track('drone', 'pad', 'always', 1, ['D2 - - - - - - - - - - - - - - -', HOLD]),
  ],
};

/** The drop was scrubbed: nothing happened, and the cue says so. */
export const DEBRIEF_SCRUBBED: Song = {
  name: 'debrief-scrubbed',
  bpm: 80,
  steps: 16,
  tracks: [track('bugle', 'bugle', 'always', 0.7, ['A3 - - - G3 - - - F3 - - - E3 - - -'])],
};

export const MEDAL_FANFARE: Song = {
  name: 'medal',
  bpm: 112,
  steps: 2 * 16,
  tracks: [
    track('lead', 'lead', 'always', 0.9, ['A4 - A4 A4 D5 - - - F#5 - E5 - D5 - - -',
      'A5^ - - - - - - - - - - - . . . .']),
    track('harmony', 'arp', 'always', 0.9, ['F#4 - F#4 F#4 A4 - - - D5 - C#5 - A4 - - -',
      'F#5 - - - - - - - - - - - . . . .']),
    track('bass', 'bass', 'always', 0.8, ['D2 - - - D2 - - - A1 - - - A1 - - -', 'D2 - - - - - - - - - - - . . . .']),
    track('snare', 'snare', 'always', 0.7, ['x . x x X . . . X . x . X . x x', 'X . . . . . . . . . . . . . . .']),
  ],
};

export const PROMOTION_STING: Song = {
  name: 'promotion',
  bpm: 104,
  steps: 2 * 16,
  tracks: [
    track('lead', 'lead', 'always', 0.9, ['D4 - - D4 F#4 - A4 - D5 - - - - - - -',
      'E5 - - - F#5^ - - - - - - - . . . .']),
    track('bass', 'bass', 'always', 0.8, ['D2 . D2 . D2 . D2 . D2 . D2 . D2 . D2 .',
      'A1 - - - D2 - - - - - - - . . . .']),
    track('snare', 'snare', 'always', 0.7, ['o o o o x x x x x x x x X X X X', 'X . . . X . . . . . . . . . . .']),
  ],
};

export const STINGS = {
  memorial: MEMORIAL,
  kia: DEBRIEF_KIA,
  success: DEBRIEF_SUCCESS,
  loss: DEBRIEF_LOSS,
  scrubbed: DEBRIEF_SCRUBBED,
  medal: MEDAL_FANFARE,
  promotion: PROMOTION_STING,
} as const;

export type StingId = keyof typeof STINGS;
